import { ElementTransformer } from '@lexical/markdown';
import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  HorizontalRuleNode,
} from '@lexical/react/LexicalHorizontalRuleNode';
import { Klass, LexicalNode } from 'lexical';

// Matches a CommonMark thematic break: a line of three or more `-`, `_`, or
// `*` characters, each optionally separated by spaces. This deliberately
// covers the spaced `- - -` form (and `* * *`, `_ _ _`) because that is the
// shape that currently leaks into slides as raw text. The trailing-space
// tolerance also lets the markdown shortcut fire after typing `---` + space.
const HORIZONTAL_RULE_REGEX =
  /^ {0,3}(?:(?:- *){3,}|(?:_ *){3,}|(?:\* *){3,})$/;

/**
 * Lexical element transformer for horizontal rules. Opt-in: it is not part of
 * the editor's default transformer set, so consumers must pass it explicitly
 * (currently only the slide editor). Modeled on Lexical's reference `HR`
 * transformer.
 */
export const horizontalRuleTransformer: ElementTransformer = {
  dependencies: [HorizontalRuleNode],
  export: (node) => ($isHorizontalRuleNode(node) ? '---' : null),
  regExp: HORIZONTAL_RULE_REGEX,
  replace: (parentNode, _children, _match, isImport) => {
    const line = $createHorizontalRuleNode();
    // When importing, or when there is content after this line, replace the
    // (empty) paragraph the marker sits on. Otherwise insert before it so the
    // trailing paragraph remains as the new caret position.
    if (isImport || parentNode.getNextSibling() != null) {
      parentNode.replace(line);
    } else {
      parentNode.insertBefore(line);
    }
    line.selectNext();
  },
  type: 'element',
};

/** Node set to register alongside {@link horizontalRuleTransformer}. */
export const HORIZONTAL_RULE_NODES: Klass<LexicalNode>[] = [HorizontalRuleNode];
